import { useEffect } from 'react';
import { Vim } from '@replit/codemirror-vim';
import { openPeriodicNote } from '../lib/openPeriodicNote';
import { useStore } from '../store/useStore';
import type { EditorHandle } from '../components/Editor';
import type { DocController } from './useDocController';

/**
 * Global keyboard shortcuts. The command palette (M3) is the discoverable
 * surface; these are the direct bindings. Keep in sync with the palette's
 * displayed shortcuts.
 */
export function useKeyboardShortcuts(
  _editorRef: React.RefObject<EditorHandle>,
  controller: DocController,
): void {
  // Vim's `:w`. markbird saves continuously, so writing means "save now" —
  // flush the pending autosave — not "save to a file", which would open a
  // picker nobody typing `:w` expects. Registered globally by the Vim plugin,
  // so re-registering on a new controller simply replaces the handler.
  useEffect(() => {
    Vim.defineEx('write', 'w', () => {
      void controller.flush();
    });
  }, [controller]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const store = useStore.getState();

      // `?` — keyboard shortcuts sheet (only when not typing in a field/editor).
      if (e.key === '?' && !e.metaKey && !e.ctrlKey) {
        const t = e.target as HTMLElement | null;
        const typing =
          !!t &&
          (t.isContentEditable ||
            /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) ||
            !!t.closest('.cm-editor'));
        if (!typing) {
          e.preventDefault();
          store.setShortcutsOpen(true);
        }
        return;
      }

      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;

      // Cmd/Ctrl+K — command palette
      if (!e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        store.setPaletteOpen(true);
        return;
      }
      // Cmd/Ctrl+Shift+D — today's daily note ("D" for daily).
      if (e.shiftKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        void openPeriodicNote('daily', new Date(), controller.open);
        return;
      }
      // Cmd/Ctrl+O — quick switcher. preventDefault matters: the browser's own
      // Cmd/Ctrl+O opens a file picker for a local HTML file.
      if (!e.shiftKey && e.key.toLowerCase() === 'o') {
        e.preventDefault();
        store.setPaletteOpen(true, 'documents');
        return;
      }
      // Cmd/Ctrl+N — new document
      if (!e.shiftKey && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        void controller.create();
        return;
      }
      // Cmd/Ctrl+\ — sidebar (Cmd/Ctrl+B is Markdown bold in the editor)
      if (e.key === '\\') {
        e.preventDefault();
        store.toggleSidebar();
        return;
      }
      // Cmd/Ctrl+Shift+V — view mode
      if (e.shiftKey && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        store.toggleViewMode();
        return;
      }
      // Cmd/Ctrl+Shift+P — switch active pane (switch mode)
      if (e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        store.toggleSwitchPane();
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
